import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const baseUrl = `${process.env.REACT_APP_API_URL}`;

const BotWidget = ({ walletAddress }) => {
  const [transactions, setTransactions] = useState([]);
  const [totalInLastDay, setTotalInLastDay] = useState(0);
  const [status, setStatus] = useState("unknown");
  const [startedAt, setStartedAt] = useState(null);
  const [lastActive, setLastActive] = useState(null);
  const [uptime, setUptime] = useState(null);
  const [loading, setLoading] = useState(false);

  const formatUptime = (seconds) => {
    if (!seconds) return "0h 0m 0s";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    return new Date(timeStr).toLocaleString();
  };

  const fetchBotStatus = async () => {
    if (!walletAddress) return;

    try {
      const res = await axios.get(`${baseUrl}/bot/status`, {
        params: { walletAddress },
      });

      const { status, startedAt, lastActive, uptimeInSeconds } = res.data;
      setStatus(status);
      setStartedAt(startedAt);
      setLastActive(lastActive);
      setUptime(uptimeInSeconds);
    } catch (error) {
      console.error(
        "Failed to fetch bot status:",
        error.response?.data || error.message
      );
      setStatus("unknown");
    }
  };

  useEffect(() => {
    fetchBotStatus();
    const interval = setInterval(() => {
      setUptime((prev) => (prev != null ? prev + 1 : null));
    }, 1000);
    return () => clearInterval(interval);
  }, [walletAddress]);

  const handleToggleBot = async () => {
    if (!walletAddress)
      return toast.warning("Please connect your wallet first.");
    const action = status === "active" ? "stop" : "start";

    try {
      setLoading(true);
      const response = await axios.post(`${baseUrl}/bot/${action}`, {
        walletAddress,
      });

      const { bot } = response.data;
      setStatus(bot.status);
      setStartedAt(bot.startedAt);
      setLastActive(bot.lastActive || null);
      setUptime(bot.startedAt ? 0 : null);
    } catch (error) {
      console.error(
        "Bot action failed:",
        error.response?.data || error.message
      );
      toast.warning("Failed to update bot status.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (walletAddress) => {
    try {
      const response = await axios.get(
        `${baseUrl}/transaction/${walletAddress}`
      );
      setTotalInLastDay(response.data.totalInLast24Hours);
    } catch (error) {
      console.error("Error fetching transaction:", error);
    }
  };

  useEffect(() => {
    if (walletAddress) {
      fetchTransactions(walletAddress);
    }
  }, [walletAddress]);

  return (
    <div className="backdrop-blur-md bg-white/70 dark:bg-slate-900/70 rounded-3xl shadow-2xl p-6 w-[28%] mx-auto border border-emerald-100 dark:border-slate-700">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-cyan-500">
          Bot Control
        </h2>
        {status === "active" && (
          <div className="text-sm text-emerald-600 dark:text-emerald-300">
            Uptime: {formatUptime(uptime)}
          </div>
        )}
      </div>

      {/* Status Section */}
      <div className="mb-2 space-y-1 text-right">
        <div
          className={`font-semibold ${
            status === "active" ? "text-emerald-600" : "text-red-500"
          }`}
        >
          ● {status === "active" ? "Active" : "Inactive"}
        </div>
        {status === "active" && startedAt && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Started At: {formatTime(startedAt)}
          </div>
        )}
        {status === "inactive" && lastActive && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Last Active: {formatTime(lastActive)}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white dark:bg-slate-800 py-4 px-3 rounded-xl text-center shadow-sm border border-emerald-100 dark:border-slate-700 min-h-[115px] flex items-center justify-center">
          <div>
            <div className="text-gray-600 dark:text-gray-300 text-base">
              Trades in Last 24h
            </div>
            <div className="text-xl font-bold text-gray-800 dark:text-white">
              {totalInLastDay}
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 py-4 px-3 rounded-xl text-center shadow-sm border border-emerald-100 dark:border-slate-700 min-h-[115px] flex items-center justify-center">
          <div>
            <div className="text-gray-600 dark:text-gray-300 text-base">
              P&amp;L Today
            </div>
            <div className="text-xl font-bold text-green-500 dark:text-green-400">
              -
            </div>
          </div>
        </div>
      </div>

      {/* Control Button */}
      <div>
        <button
          onClick={handleToggleBot}
          disabled={loading}
          className={`w-full px-6 py-3 rounded-xl font-semibold text-white transition-all ${
            status === "active"
              ? "bg-gradient-to-r from-gray-400 to-gray-600 opacity-80 cursor-not-allowed"
              : "bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 shadow-md"
          }`}
        >
          {status === "active" ? "Stop Bot" : "Start Bot"}
        </button>
      </div>
    </div>
  );
};

export default BotWidget;
